import { useSessionStore } from "../../../state/src/useSessionStore";
import { ConsumerProfileBaseInfo } from "../view-models/ConsumerProfileBaseInfo";
import { ServiceMessageMethod } from "../view-models/ServiceMessageMethod";
import { GenderType } from "../view-models/GenderType";
import { SendMoneyPostUpdateConsumerProfileParameter } from "../view-models/SendMoneyPostUpdateConsumerProfileParameter";
import { toTwoLetterStateCode } from "./addressFieldValidation";

export type UpdateConsumerProfileDraft = {
	consumerLoginId: string | null;
	pin: string | null;
	freqCustCardNumber: string | null;
	isTermsAndConditionsChecked: boolean | null;
	lastAcceptedDate: Date | null;
	serviceMessageMethod: ServiceMessageMethod | null;
	partialConsumerInfo: ConsumerProfileBaseInfo;
};

export type UpdateConsumerProfileRequestPayload = {
	unitProfileID: string | null;
	agentID: string | null;
	token: string;
	timeStamp: string;
	apiVersion: string;
	clientSoftwareVersion: string;
	consumerLoginID: string | null;
	lastTcAcceptedDate?: string | null;
	consumerPIN: string | null;
	partialConsumerInfo: Record<string, any>;
};

/**
 * Builds a Consumer Profile update draft using the current Zustand store values.
 * This does NOT perform any API calls. Intended to validate data collection only.
 */
export function buildUpdateConsumerProfileDraft(partialOverrides?: Partial<{
	receiverFirstName: string | null;
	receiverMiddleName: string | null;
	receiverLastName: string | null;
	receiverLastName2: string | null;
	// In case some sender values are passed directly from steps later
	senderFirstName: string | null;
	senderMiddleName: string | null;
	senderLastName: string | null;
	senderLastName2: string | null;
	senderAddress: string | null;
	senderCity: string | null;
	senderState: string | null;
	senderZipCode: string | null;
	senderCountry: string | null;
	senderDOB: Date | null;
	senderHomePhone: string | null;
	senderNationalityCountry: string | null;
	senderPhotoIdCountry: string | null;
	senderPhotoIdNumber: string | null;
	senderPhotoIdType: string | null;
	senderLegalIdNumber: string | null;
	senderLegalIdType: string | null;
	consumerId: string | null;
	idIssueAuthority: string | null;
	idIssueDate: Date | null;
	enableTextMessage: boolean | null;
}>) : UpdateConsumerProfileDraft {
	const s = (useSessionStore as any).getState?.() || {};

	// Values we might have in store already
	const agentId: string | null = s.agentId ?? null;
	const unitProfileId: string | null = s.unitProfileId ?? null;
	const selectedDestinationCountryCode: string | null = s.selectedDestinationCountryCode ?? null;
	const selectedIdPhotoIdStateAbbr: string | null = s.selectedIdPhotoIdStateAbbr ?? null;
	const selectedReceiveCurrency: string | null = s.selectedReceiveCurrency ?? null;
	const selectedIdTypeKey: string | null = s.selectedIdTypeKey ?? null;
	const selectedIdTypeValue: string | null = s.selectedIdTypeValue ?? null;
	const lastFeeLookupResponse: any = s.lastFeeLookupResponse ?? null;
	const selectedIdPhotoIdCountry: string | null = s.selectedIdPhotoIdCountry ?? null;
	const gffpFieldsByCategory: Record<string, any[]> = s.gffpFieldsByCategory ?? {};

	// Unknown in current store schema; allow overrides and fallbacks to null
	const consumerLoginId: string | null = (partialOverrides as any)?.consumerLoginId ?? (s.phoneNumber ?? null);
	const pin: string | null = (partialOverrides as any)?.pin ?? (s.consumerPin ?? null);
	const freqCustCardNumber: string | null = null;
	const isTermsAndConditionsChecked: boolean | null = null;

	const enableTextMessage: boolean | null = partialOverrides?.enableTextMessage ?? null;
	const serviceMessageMethod: ServiceMessageMethod | null = enableTextMessage ? ServiceMessageMethod.SMS : null;

	// Assemble ConsumerInfo (mostly sender related per legacy API shape)
	const partialConsumerInfo: ConsumerProfileBaseInfo = {
		SenderFirstName: coalesceStr(partialOverrides?.senderFirstName ?? s.senderFirstName),
		SenderMiddleName: coalesceStr(partialOverrides?.senderMiddleName ?? s.senderMiddleName),
		SenderLastName: coalesceStr(partialOverrides?.senderLastName ?? s.senderLastName),
		SenderLastName2: coalesceStr(partialOverrides?.senderLastName2 ?? s.senderLastName2),
		SenderAddressTypeCode: undefined,
		SenderAddress: coalesceStr(partialOverrides?.senderAddress ?? s.senderAddress),
		SenderAddress2: undefined,
		SenderAddress3: undefined,
		SenderAddress4: undefined,
		SenderCity: coalesceStr(partialOverrides?.senderCity ?? s.senderCity),
		// API accepts only 2-letter senderState; omit when we only have a full name / nothing.
		SenderState: coalesceStr(
			toTwoLetterStateCode(partialOverrides?.senderState ?? s.senderState) || null
		),
		SenderZipCode: coalesceStr(partialOverrides?.senderZipCode ?? s.senderZipCode),
		SenderCountry: coalesceStr(partialOverrides?.senderCountry ?? s.senderCountry) || 'USA',
		SenderDOB: partialOverrides?.senderDOB ?? s.senderDOB ?? parseDOBString(s.senderDOBString),
		SenderCountryDialInCode: undefined,
		SenderHomePhone: s.phoneNumber,
		ConsumerId: coalesceStr(partialOverrides?.consumerId ?? s.consumerId),
		ServiceMessageMethod: serviceMessageMethod ?? undefined,
		ProfileLockedOut: false,
		SenderBirthCity: undefined,
		SenderBirthState: undefined,
		SenderBirthCountry: undefined,
		SenderGender:  GenderType.M,
		SenderFiscalCode: undefined,
		SenderNationalityCountry: coalesceStr(partialOverrides?.senderNationalityCountry),
		SenderPhotoIdType: coalesceStr(partialOverrides?.senderPhotoIdType ?? selectedIdTypeValue ?? selectedIdTypeKey),
		SenderPhotoIdIssueDate: formatDate(partialOverrides?.idIssueDate) ?? undefined,
		SenderPhotoIdExpDate: undefined,
		SenderPhotoIdNumber: coalesceStr(partialOverrides?.senderPhotoIdNumber ?? s.senderPhotoIdNumber),
		SenderPhotoIdIssueOffice: undefined,
		SenderPhotoIdIssuingAuthority: coalesceStr(partialOverrides?.idIssueAuthority),
		SenderPhotoIdIssueCity: undefined,
		SenderPhotoIdIssueState: coalesceStr(selectedIdPhotoIdStateAbbr),
		SenderPhotoIdIssueCountry: selectedIdPhotoIdCountry ?? 'USA',
		SenderIdVerificationStatus: undefined,
		SenderIdVerificationDate: partialOverrides?.idIssueDate ?? null,
		SenderPhotoIdVerifiedBy: undefined,
		IdVerficationMethod: undefined,
		IdVerifiedDate: null,
		LastUpdated: null
	};

	const lastAcceptedDate: Date | null = isTermsAndConditionsChecked ? new Date() : null;

	return {
		consumerLoginId,
		pin,
		freqCustCardNumber,
		isTermsAndConditionsChecked,
		lastAcceptedDate,
		serviceMessageMethod,
		partialConsumerInfo
	};
}

export function logUpdateConsumerProfileDraft(partialOverrides?: Parameters<typeof buildUpdateConsumerProfileDraft>[0]): SendMoneyPostUpdateConsumerProfileParameter {
	const draft = buildUpdateConsumerProfileDraft(partialOverrides);

	const param: SendMoneyPostUpdateConsumerProfileParameter = {
		ConsumerLoginID: draft.consumerLoginId ?? undefined,
		ItemChoiceType: "PIN",
		Item: draft.pin ?? undefined,
		PartialConsumerInfo: draft.partialConsumerInfo ? [draft.partialConsumerInfo] : undefined,
		freqCustCardNumber: draft.freqCustCardNumber ?? undefined,
		isTCChecked: draft.isTermsAndConditionsChecked ?? undefined,
		lastAcceptedDate: draft.lastAcceptedDate ?? null,
	};

	// Keep logs compact for dev console
	// eslint-disable-next-line no-console
	console.log("SendMoneyPostUpdateConsumerProfileParameter:", {
		ConsumerLoginID: param.ConsumerLoginID,
		ItemChoiceType: param.ItemChoiceType,
		Item: mask(param.Item || null),
		freqCustCardNumber: mask(param.freqCustCardNumber || null),
		isTCChecked: param.isTCChecked,
		lastAcceptedDate: param.lastAcceptedDate,
		PartialConsumerInfo: param.PartialConsumerInfo,
	});

	return param;
}

export function buildUpdateConsumerProfileRequestPayload(partialOverrides?: Parameters<typeof buildUpdateConsumerProfileDraft>[0]): UpdateConsumerProfileRequestPayload {
	const s = (useSessionStore as any).getState?.() || {};
	const draft = buildUpdateConsumerProfileDraft(partialOverrides);

	const unitProfileID: string | null = s.unitProfileId ?? null;
	const agentID: string | null = s.agentId ?? null;
	const token = "MAATEST";
	const timeStamp = new Date().toISOString();
	const apiVersion = "1509";
	const clientSoftwareVersion = "1509";
	const consumerLoginID = draft.consumerLoginId;
	const lastTcAcceptedDate = draft.lastAcceptedDate ? new Date(draft.lastAcceptedDate).toISOString() : undefined;
	const consumerPIN = draft.pin;

	// Map ConsumerProfileBaseInfo (PascalCase) into request partialConsumerInfo (camelCase)
	const ci = draft.partialConsumerInfo || {} as ConsumerProfileBaseInfo;
	const partialConsumerInfoRaw: Record<string, any> = {
		senderFirstName: ci.SenderFirstName,
		senderMiddleName: ci.SenderMiddleName,
		senderLastName: ci.SenderLastName,
		senderLastName2: ci.SenderLastName2,
		senderAddress: ci.SenderAddress,
		senderCity: ci.SenderCity,
		senderState: ci.SenderState,
		senderZipCode: ci.SenderZipCode,
		senderCountry: ci.SenderCountry,
		senderDOB: ci.SenderDOB ? formatDate(ci.SenderDOB) : undefined,
		senderHomePhone: consumerLoginID,
		profileLockedOut: ci.ProfileLockedOut ?? false,
		senderGender: ci.SenderGender,
		senderPhotoIdType: ci.SenderPhotoIdType,
		senderPhotoIdNumber: ci.SenderPhotoIdNumber,
		senderPhotoIdIssueCountry: ci.SenderPhotoIdIssueCountry,
	};
	// Remove undefined/null keys to keep payload clean
	const partialConsumerInfo: Record<string, any> = Object.fromEntries(
		Object.entries(partialConsumerInfoRaw).filter(([, v]) => v !== undefined && v !== null)
	);

	return {
		unitProfileID,
		agentID,
		token,
		timeStamp,
		apiVersion,
		clientSoftwareVersion,
		consumerLoginID,
		lastTcAcceptedDate: lastTcAcceptedDate ?? null,
		consumerPIN,
		partialConsumerInfo,
	};
}

export function logUpdateConsumerProfileRequestPayload(partialOverrides?: Parameters<typeof buildUpdateConsumerProfileDraft>[0]) {
	const payload = buildUpdateConsumerProfileRequestPayload(partialOverrides);
	// eslint-disable-next-line no-console
	console.log("updateConsumerProfileRequest payload:", payload);
	return payload;
}

function coalesceStr(v?: string | null): string | undefined {
	if (v === null || v === undefined) return undefined;
	const str = String(v).trim();
	return str.length ? str : undefined;
}

function formatDate(d?: Date | null): string | null {
	if (!d) return null;
	const yyyy = d.getFullYear();
	const mm = String(d.getMonth() + 1).padStart(2, "0");
	const dd = String(d.getDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}`;
}

function parseDOBString(mmddyyyy?: string | null): Date | null {
	try {
		if (!mmddyyyy) return null;
		const parts = String(mmddyyyy).split("/");
		if (parts.length !== 3) return null;
		const mm = parseInt(parts[0], 10);
		const dd = parseInt(parts[1], 10);
		const yyyy = parseInt(parts[2], 10);
		if (!isFinite(mm) || !isFinite(dd) || !isFinite(yyyy)) return null;
		// Create date at noon local time to avoid timezone boundary issues
		// This ensures the date doesn't shift when serialized to JSON
		return new Date(yyyy, mm - 1, dd, 12, 0, 0, 0);
	} catch {
		return null;
	}
}

function pushIfMissing(target: string[], value: any, name: string) {
	if (value === undefined || value === null || String(value).trim() === "") {
		target.push(name);
	}
}

function mask(v: string | null): string | null {
	if (!v) return v;
	return v.replace(/.(?=.{2})/g, "*");
}

function summarize(obj: any) {
	try {
		if (!obj) return null;
		if (typeof obj !== "object") return obj;
		const keys = Object.keys(obj);
		return { type: obj?.constructor?.name || "object", keys: keys.slice(0, 8) };
	} catch {
		return null;
	}
}
